import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { RollbarService } from '../../../cross-team-shared/messaging/rollbar.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { PersistenceService } from '../../shared/misc-services/persistence.service';
import { annotationQueueName } from '../../shared/resources/annotation-resource.service';

export type SubmissionStep =
  | 'submissionStarted'
  | 'upsertAnswerStarted'
  | 'autoQAStarted'
  | 'sh3SubmissionStarted'
  | 'submissionCompleted';

const shortSubmissionTreshold = 10_000;

@Injectable()
export class SubmissionLoggingService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private persistenceService = inject(PersistenceService);
  private rollbarService = inject(RollbarService);

  private submissionStartTime?: number;
  private upsertAnswerStartedTime?: number;
  private autoQAStartTime?: number;
  private sh3SubmissionStarted?: number;
  private pendingOperations?: number;

  // eslint-disable-next-line complexity -- manage all single submission status
  updateStep(status: SubmissionStep) {
    switch (status) {
      case 'submissionStarted':
        this.submissionStartTime = performance.now();
        this.setCurrentPendingOperations();
        break;

      case 'upsertAnswerStarted':
        this.upsertAnswerStartedTime = performance.now();
        break;

      case 'autoQAStarted':
        this.autoQAStartTime = performance.now();
        break;

      case 'sh3SubmissionStarted':
        this.sh3SubmissionStarted = performance.now();
        break;

      case 'submissionCompleted':
        this.logForLongSubmission();
        break;
    }
  }

  private setCurrentPendingOperations() {
    this.pendingOperations =
      this.persistenceService.getQueueCounter(annotationQueueName);
  }

  private logForLongSubmission() {
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return;
    }

    const now = performance.now();
    this.assertAllTimes();

    const totalElapsedTime = now - this.submissionStartTime!;

    if (totalElapsedTime < shortSubmissionTreshold) {
      return;
    }

    this.rollbarService.error('submission took too long', {
      totalUnsavedAnnotations: this.pendingOperations!,
      totalPendingAnnotationTime: this.upsertAnswerStartedTime
        ? this.upsertAnswerStartedTime - this.submissionStartTime!
        : 0,
      totalAutoQATime: this.sh3SubmissionStarted! - this.autoQAStartTime!,
      totalSubmissionOperationTime: now - this.sh3SubmissionStarted!,
    });
  }

  private assertAllTimes() {
    assert(
      isDefined(this.submissionStartTime) &&
        (this.taskWorkspaceStore.isUsingGenAi() ||
          isDefined(this.upsertAnswerStartedTime)) &&
        isDefined(this.autoQAStartTime) &&
        isDefined(this.sh3SubmissionStarted),
      'One of the submission steps were not correctly recorded',
    );
  }
}
